import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Alert,
  TextField,
  Button,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  FileUpload as FileUploadIcon,
  Download as DownloadIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import Papa from "papaparse";
import { format } from "date-fns";

import {
  usePlantManagement,
  usePlantForm,
} from "../../hooks/usePlantManagement";
import { themeColors } from "../../utils/content";

// Components
import PlantTable from "./Plantlist/planttable";
import PlantFormDialog from "./Plantlist/dialogs/plantformdialog";
import ImportPlantsDialog from "./Plantlist/dialogs/importplantsdialog";
import DeleteConfirmDialog from "./Plantlist/dialogs/deleteconfirmdialog";

const PlantList = () => {
  const {
    currentPlants,
    orgPlants,
    isLoading,
    activeTab,
    searchQuery,
    selectedPlants,
    setActiveTab,
    setSearchQuery,
    togglePlantSelection,
    clearSelection,
    loadPlants,
    createPlant,
    updatePlant,
    deletePlant,
    deallocatePlant,
    importPlants,
  } = usePlantManagement();

  const {
    formData,
    updateField,
    validateForm,
    resetForm,
    setFormDataFromPlant,
    isValid,
  } = usePlantForm();

  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuPlantId, setActionMenuPlantId] = useState(null);
  const [selectedPlant, setSelectedPlant] = useState(null);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    loadPlants().catch((err) => {
      console.error("Error loading plants:", err);
      showSnackbar("Failed to load plants", "error");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadPlants]);

  // Utility functions
  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleSnackbarClose = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const formatDate = useCallback((dateValue) => {
    if (!dateValue) return "";
    try {
      const date =
        typeof dateValue === "number"
          ? new Date(dateValue * 1000)
          : new Date(dateValue);
      return format(date, "dd-MMM-yyyy");
    } catch (error) {
      return dateValue.toString();
    }
  }, []);

  // Event handlers
  const handleTabChange = useCallback(
    (_, newValue) => {
      if (newValue !== null) {
        setActiveTab(newValue);
        setSearchQuery("");
      }
    },
    [setActiveTab, setSearchQuery]
  );

  const handleSearchChange = useCallback(
    (event) => {
      setSearchQuery(event.target.value);
    },
    [setSearchQuery]
  );

  // Action menu handlers
  const handleActionMenuOpen = useCallback((event, plantId) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setActionMenuPlantId(plantId);
  }, []);

  const handleActionMenuClose = useCallback(() => {
    setActionMenuAnchor(null);
    setActionMenuPlantId(null);
  }, []);

  // Dialog handlers
  const openAddDialog = useCallback(() => {
    resetForm();
    setSelectedPlant(null);
    setAddDialogOpen(true);
  }, [resetForm]);

  const openEditDialog = useCallback(
    (plant) => {
      setSelectedPlant(plant);
      setFormDataFromPlant(plant);
      setEditDialogOpen(true);
      handleActionMenuClose();
    },
    [setFormDataFromPlant, handleActionMenuClose]
  );

  const openDeleteDialog = useCallback(
    (plant) => {
      setSelectedPlant(plant);
      setDeleteDialogOpen(true);
      handleActionMenuClose();
    },
    [handleActionMenuClose]
  );

  const openImportDialog = useCallback(() => {
    clearSelection();
    setImportDialogOpen(true);
  }, [clearSelection]);

  const closeDialogs = useCallback(() => {
    setAddDialogOpen(false);
    setEditDialogOpen(false);
    setDeleteDialogOpen(false);
    setImportDialogOpen(false);
    setSelectedPlant(null);
    resetForm();
  }, [resetForm]);

  // CRUD operations
  const handleCreatePlant = useCallback(async () => {
    if (!validateForm()) {
      showSnackbar("Please fill in all required fields", "error");
      return;
    }

    try {
      const { id, ...plantDataWithoutId } = formData;
      await createPlant(plantDataWithoutId);
      showSnackbar("Plant created successfully");
      closeDialogs();
    } catch (error) {
      console.error("Error creating plant:", error);
      showSnackbar("Failed to create plant", "error");
    }
  }, [validateForm, createPlant, formData, showSnackbar, closeDialogs]);

  const handleUpdatePlant = useCallback(async () => {
    if (!selectedPlant || !validateForm()) {
      showSnackbar("Please fill in all required fields", "error");
      return;
    }
    try {
      const plantId = selectedPlant.id;
      const { id, ...plantDataForUpdate } = formData;
      const plantDataToUpdate = { ...plantDataForUpdate, id: plantId };
      
      await updatePlant({ plantId: plantId, plantData: plantDataToUpdate });
      showSnackbar("Plant updated successfully");
      closeDialogs();
    } catch (error) {
      console.error("Error updating plant:", error);
      showSnackbar("Failed to update plant", "error");
    }
  }, [
    selectedPlant,
    validateForm,
    updatePlant,
    formData,
    showSnackbar,
    closeDialogs,
  ]);

  const handleDeletePlant = useCallback(async () => {
    if (!selectedPlant) return;

    try {
      await deletePlant(selectedPlant.id);
      showSnackbar("Plant deleted successfully");
      closeDialogs();
    } catch (error) {
      console.error("Error deleting plant:", error);
      showSnackbar("Failed to delete plant", "error");
    }
  }, [selectedPlant, deletePlant, showSnackbar, closeDialogs]);

  const handleDeallocatePlant = useCallback(
    async (plantId) => {
      try {
        await deallocatePlant(plantId);
        showSnackbar("Plant deallocated successfully");
        handleActionMenuClose();
      } catch (error) {
        console.error("Error deallocating plant:", error);
        showSnackbar("Failed to deallocate plant", "error");
      }
    },
    [deallocatePlant, showSnackbar, handleActionMenuClose]
  );

  const handleImportPlants = useCallback(async () => {
    if (selectedPlants.length === 0) {
      showSnackbar("Please select at least one plant to import", "warning");
      return;
    }

    try {
      await importPlants(selectedPlants);
      showSnackbar(`Successfully imported ${selectedPlants.length} plants`);
      closeDialogs();
    } catch (error) {
      console.error("Error importing plants:", error);
      showSnackbar("Failed to import plants", "error");
    }
  }, [selectedPlants, importPlants, showSnackbar, closeDialogs]);

  const handleDirectImport = useCallback(
    async (plantId) => {
      try {
        await importPlants([plantId]);
        showSnackbar("Plant imported successfully");
        handleActionMenuClose();
      } catch (error) {
        console.error("Error importing plant:", error);
        showSnackbar("Failed to import plant", "error");
      }
    },
    [importPlants, showSnackbar, handleActionMenuClose]
  );

  // CSV download
  const handleDownloadCSV = useCallback(() => {
    if (!currentPlants || currentPlants.length === 0) {
      showSnackbar("No data to download", "warning");
      return;
    }

    try {
      const csvData = currentPlants.map((plant) => ({
        Nickname: plant.nickname || plant.name || "",
        "Plant ID": plant.plant_id || "",
        "Plant Type": plant.plant_type || "",
        Make: plant.make || "",
        Model: plant.model || "",
        "Registration Number": plant.registration_number || "",
        "Date on Site": plant.date_on_site
          ? formatDate(plant.date_on_site)
          : "",
        "Documents Count": (plant.document || plant.documents || []).length,
      }));

      const csv = Papa.unparse(csvData);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute(
        "download",
        `${activeTab === 0 ? "project" : "org"}-plant-list.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading CSV:", error);
      showSnackbar("Failed to download CSV", "error");
    }
  }, [currentPlants, activeTab, formatDate, showSnackbar]);

  // Form field handlers
  const handleFieldChange = useCallback(
    (field, value) => {
      updateField(field, value);
    },
    [updateField]
  );

  const handleDateChange = useCallback(
    (date) => {
      updateField("date_on_site", date);
    },
    [updateField]
  );

  const handleDocumentsChange = useCallback(
    (documents) => {
      updateField("document", documents);
    },
    [updateField]
  );

  return (
    <Box>
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
            Plant List
          </Typography>
          
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <TextField
                placeholder="Search plants..."
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={handleSearchChange}
                sx={{
                  width: 250,
                  transition: "width 0.3s",
                  "& .MuiInputBase-root": { borderRadius: 1 },
                }}
                inputProps={{ min: 0, style: { fontSize: '16px', height: "45px" } }}
              />
                <ToggleButtonGroup
                  value={activeTab}
                  exclusive
                  onChange={handleTabChange}
                  aria-label="plant type"
                  sx={{
                    border: `1px solid ${themeColors.borderColor}`,
                    ".MuiToggleButton-root": {
                      border: "none",
                      px: 2,
                      textTransform: "capitalize",
                      fontWeight: 500,
                      color: themeColors.textSecondary,
                      "&.Mui-selected": {
                        bgcolor: themeColors.primary,
                        color: themeColors.white,
                        "&:hover": {
                          bgcolor: themeColors.primaryHover,
                        },
                      },
                      "&:hover": {
                        bgcolor: "rgba(241, 98, 83, 0.08)",
                      },
                    },
                  }}
                >
            <ToggleButton value={0} aria-label="project plant">
              Project Plant
            </ToggleButton>
            <ToggleButton value={1} aria-label="org plant">
              Org Plant
            </ToggleButton>
          </ToggleButtonGroup>

          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadCSV}
            sx={{
              borderColor: themeColors.primary,
              color: themeColors.primary,
              "&:hover": {
                borderColor: themeColors.primaryHover,
                bgcolor: "rgba(241, 98, 83, 0.08)",
              },
              textTransform: "none",
            }}
          >
            Download
          </Button>

          {activeTab === 0 ? (
            <Button
              variant="contained"
              sx={{
                bgcolor: themeColors.primary,
                "&:hover": { bgcolor: themeColors.primaryHover },
                textTransform: "none",
              }}
              startIcon={<FileUploadIcon />}
              onClick={openImportDialog}
            >
              Import Plant
            </Button>
          ) : (
            <Button
              variant="contained"
              sx={{
                bgcolor: themeColors.primary,
                "&:hover": { bgcolor: themeColors.primaryHover },
                textTransform: "none",
              }}
              startIcon={<AddIcon />}
              onClick={openAddDialog}
            >
              Add Plant
            </Button>
          )}
          </Box>
        </Box>
      </Box>

      <PlantTable
        plants={currentPlants}
        loading={isLoading}
        onActionMenuOpen={handleActionMenuOpen}
        activeTab={activeTab}
        formatDate={formatDate}
      />

      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          elevation: 3,
          sx: { minWidth: 180, mt: 0.5 },
        }}
      >
        <MenuItem
          onClick={() => {
            const plant = currentPlants.find((p) => p.id === actionMenuPlantId);
            if (plant) openEditDialog(plant);
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" sx={{ color: themeColors.primary }} />
          </ListItemIcon>
          <ListItemText primary="Edit" />
        </MenuItem>

        {activeTab === 0 ? (
          <MenuItem
            onClick={() => {
              const plant = currentPlants.find(
                (p) => p.id === actionMenuPlantId
              );
              if (plant && plant.plant_project_id) {
                handleDeallocatePlant(plant.plant_project_id);
              } else {
                showSnackbar("Could not find plant project ID", "error");
              }
            }}
          >
            <ListItemIcon>
              <CloseIcon fontSize="small" sx={{ color: themeColors.primary }} />
            </ListItemIcon>
            <ListItemText primary="Deallocate" />
          </MenuItem>
        ) : (
          <>
            <MenuItem
              onClick={() => {
                const plant = currentPlants.find(
                  (p) => p.id === actionMenuPlantId
                );
                if (plant) openDeleteDialog(plant);
              }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" sx={{ color: themeColors.primary }} />
              </ListItemIcon>
              <ListItemText primary="Delete" />
            </MenuItem>
            <MenuItem onClick={() => handleDirectImport(actionMenuPlantId)}>
              <ListItemIcon>
                <FileUploadIcon fontSize="small" sx={{ color: themeColors.primary }} />
              </ListItemIcon>
              <ListItemText primary="Import to Project" />
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Add Plant Dialog */}
      <PlantFormDialog
        open={addDialogOpen}
        onClose={closeDialogs}
        title="Add Plant Details"
        formData={formData}
        onFieldChange={handleFieldChange}
        onDateChange={handleDateChange}
        onDocumentsChange={handleDocumentsChange}
        onSubmit={handleCreatePlant}
        loading={isLoading}
        isValid={isValid && formData.nickname && formData.plant_id}
        isEditMode={false}
      />

      {/* Edit Plant Dialog */}
      <PlantFormDialog
        open={editDialogOpen}
        onClose={closeDialogs}
        title="Edit Plant Details"
        formData={formData}
        onFieldChange={handleFieldChange}
        onDateChange={handleDateChange}
        onDocumentsChange={handleDocumentsChange}
        onSubmit={handleUpdatePlant}
        loading={isLoading}
        isValid={isValid && formData.nickname && formData.plant_id}
        isEditMode={true}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={closeDialogs}
        onConfirm={handleDeletePlant}
        plantName={selectedPlant?.nickname || selectedPlant?.name}
        loading={isLoading}
      />

      {/* Import Plants Dialog */}
      <ImportPlantsDialog
        open={importDialogOpen}
        onClose={closeDialogs}
        orgPlants={orgPlants}
        selectedPlants={selectedPlants}
        onPlantToggle={togglePlantSelection}
        onImport={handleImportPlants}
        loading={isLoading}
      />

      {/* Snackbar notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlantList;